import React, { useMemo, useState } from "react";
import { Autocomplete, Chip, TextField } from "@mui/material";

interface TimezoneMultiSelectProps {
  label?: string;
  value?: string[];
  onChange: (timezones: string[]) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  fullWidth?: boolean;
}

const ALL_TIMEZONES: string[] = (() => {
  try {
    return (Intl as any).supportedValuesOf("timeZone");
  } catch {
    return ["UTC", "Europe/London", "Europe/Istanbul", "Asia/Kolkata", "America/New_York", "America/Los_Angeles", "Australia/Sydney"];
  }
})();

const getUTCOffset = (tz: string): string => {
  try {
    const parts = new Intl.DateTimeFormat("en", {
      timeZone: tz,
      timeZoneName: "shortOffset",
    }).formatToParts(new Date());
    const offset = parts.find((p) => p.type === "timeZoneName")?.value ?? "UTC";
    return offset.replace("GMT", "UTC");
  } catch {
    return "UTC";
  }
};

const TIMEZONE_LABELS: Record<string, string> = Object.fromEntries(
  ALL_TIMEZONES.map((tz) => [tz, `${tz} (${getUTCOffset(tz)})`])
);

const TimezoneMultiSelect = ({
  label = "Select Timezones",
  value = [],
  onChange,
  error,
  helperText,
  disabled = false,
  fullWidth = true,
}: TimezoneMultiSelectProps) => {
  const [inputValue, setInputValue] = useState("");

  const options = useMemo(() => {
    const lower = inputValue.toLowerCase();
    const filtered = lower
      ? ALL_TIMEZONES.filter((tz) => (TIMEZONE_LABELS[tz] ?? tz).toLowerCase().includes(lower))
      : ALL_TIMEZONES;
    const missing = value.filter((v) => !filtered.includes(v));
    return [...missing, ...filtered];
  }, [inputValue, value]);

  return (
    <Autocomplete
      multiple
      fullWidth={fullWidth}
      disabled={disabled}
      value={value}
      onChange={(_event, newValue) => {
        onChange(newValue);
        setInputValue("");
      }}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue, reason) => {
        if (reason === "input") {
          setInputValue(newInputValue);
        }
      }}
      isOptionEqualToValue={(option, val) => option === val}
      getOptionLabel={(option) => TIMEZONE_LABELS[option] ?? option}
      options={options}
      noOptionsText="No timezones found"
      ListboxProps={{ style: { maxHeight: "400px" } }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option}
            label={TIMEZONE_LABELS[option] ?? option}
            size="small"
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          error={Boolean(error)}
          helperText={helperText}
        />
      )}
    />
  );
};

export default TimezoneMultiSelect;
